import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dynStyle, dynVariadic, scheduleForCleanup } from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import { internalteacherid } from "../../sql/classy/types";
import * as planningstyle from "../../styles/planning.css.classes";
import * as style from "../../styles/style.css.classes";
import { colors, makeGradient } from "../lib/colors";
import { HHmm } from "../render";
import { isFunction, isNil, isObject, isString } from "../lib/istype";
import { range } from "../lib/range";
import { isEqual } from "../lib/isequal";

type specialContents =
  | null
  | { tag: "holiday"; description: string }
  | { tag: "past" }
  | { tag: "today" };

export type calendar_slot = {
  time: joda.LocalTime | Source<joda.LocalTime>;
  duration: number | Source<number>;
  weekday: joda.DayOfWeek;
  contents: string | HTMLElement;
  colors: colors | [colors, colors];
  onclick: null | { href: string } | (() => void);
  className?: string;
  internalteacherid: internalteacherid;
};

export function renderWeekCalendar(opts: {
  hours: [number, number];
  weekdays: Array<
    [
      joda.DayOfWeek,
      {
        label: string;
        sublabel?: string;
        link?: string;
        contents: specialContents;
      },
    ]
  >;
  alwaysShowGroups: boolean;
  groupByTeachers: {
    internalteacherid: internalteacherid;
    firstname: string;
  }[];
  collapseEmptyTeachers: boolean;
  slots: calendar_slot[];
  onMouseMove?: (
    position: {
      weekday: joda.DayOfWeek;
      time: joda.LocalTime;
      internalteacherid: internalteacherid;
    },
    showPreviewSlot: (slot: calendar_slot & { onclick: null }) => void,
  ) => void;
  onClickOutsideSlot?: (position: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    internalteacherid: internalteacherid;
  }) => void;
  style?: {
    headerStickyPosition?: number;
  };
}) {
  const hourHeight = 80;
  const minuteHeight = hourHeight / 60;

  let onMouseMoveElement: HTMLElement | null = null;

  return (
    <table className={planningstyle.calendar}>
      <tbody>
        <tr
          style={`position: sticky; top: -${
            opts.style?.headerStickyPosition || 24
          }px; z-index: 1000;`}
        >
          <th></th>
          {opts.weekdays.map(([_w, wdspecs]) => {
            const contents = [
              <span className={planningstyle.daynumber}>{wdspecs.label}</span>,
              <span>{wdspecs.sublabel ?? ""}</span>,
            ];
            return (
              <th>
                {isNil(wdspecs.link) ? (
                  contents
                ) : (
                  <a href={wdspecs.link}>{contents}</a>
                )}
              </th>
            );
          })}
        </tr>
        <tr>
          <td>
            {range(opts.hours[0], opts.hours[1]).map((hour) => (
              <div style={`height: ${hourHeight}px`}>
                {joda.LocalTime.of(hour).format(HHmm)}
              </div>
            ))}
          </td>
          {opts.weekdays.map(([wd, wdspecs]) => {
            const slotsForWd = opts.slots.filter((sl) =>
              isEqual(sl.weekday, wd),
            );
            const multipleTeachersSelected = opts.groupByTeachers.length > 1;
            const collapsedTeachers_ =
              opts.collapseEmptyTeachers === false
                ? opts.groupByTeachers
                : opts.groupByTeachers.filter((teacher) =>
                    slotsForWd.some(
                      (sl) =>
                        sl.internalteacherid === teacher.internalteacherid,
                    ),
                  );
            // It's a problem if ZERO teachers get rendered. onClickOutsideSlot doesn't work anymore then...
            const collapsedTeachers =
              collapsedTeachers_.length === 0 && opts.groupByTeachers.length > 0
                ? [opts.groupByTeachers[0]]
                : collapsedTeachers_;
            const renderedSlotsPerTeacher = collapsedTeachers.map(
              (teacher, i) => {
                const slots = slotsForWd
                  .filter(
                    (sl) => sl.internalteacherid === teacher.internalteacherid,
                  )
                  .orderBy((sl) =>
                    isSource(sl.time)
                      ? sl.time.get().toSecondOfDay()
                      : sl.time.toSecondOfDay(),
                  );

                const groupWidth = 100 / collapsedTeachers.length;
                const position = `position: absolute; width: ${groupWidth}%; left: ${
                  i * groupWidth
                }%; height: 100%;`;
                const containerPerTeacher = (
                  <div style={position}>
                    {slots.map((sl, slI) =>
                      renderSlot(minuteHeight, opts.hours[0], sl, {
                        addTeacherNameOnTop:
                          (multipleTeachersSelected || opts.alwaysShowGroups) &&
                          slI === 0
                            ? teacher.firstname
                            : false,
                      }),
                    )}
                  </div>
                );

                containerPerTeacher.addEventListener(
                  "click",
                  function (ev: MouseEvent) {
                    if (
                      !!ev.target &&
                      elementOrParentHasClass(
                        ev.target as HTMLElement,
                        planningstyle.slot,
                      )
                    ) {
                      // Clicked on slot -> we do nothing
                    } else {
                      // Clicked outside of slot
                      if (!opts.onClickOutsideSlot) {
                        return;
                      }
                      opts.onClickOutsideSlot({
                        internalteacherid: teacher.internalteacherid,
                        weekday: wd,
                        time: getLocalTimeFromMouseEventOnWeekdayCell(
                          minuteHeight,
                          opts.hours[0],
                          weekdaycell,
                          ev,
                        ),
                      });
                    }
                  },
                );
                containerPerTeacher.addEventListener(
                  "mousemove",
                  function (ev: MouseEvent) {
                    if (!opts.onMouseMove) {
                      return;
                    }

                    const time = getLocalTimeFromMouseEventOnWeekdayCell(
                      minuteHeight,
                      opts.hours[0],
                      weekdaycell,
                      ev,
                    );
                    if (onMouseMoveElement) {
                      onMouseMoveElement.parentElement?.removeChild(
                        onMouseMoveElement,
                      );
                    }
                    onMouseMoveElement = null;
                    opts.onMouseMove(
                      {
                        weekday: wd,
                        time: time,
                        internalteacherid: teacher.internalteacherid,
                      },
                      (previewSlot) => {
                        const renderedNewSlot = renderSlot(
                          minuteHeight,
                          opts.hours[0],
                          {
                            ...previewSlot,
                            className:
                              (previewSlot.className || "") +
                              planningstyle.selected,
                          },
                          {
                            addTeacherNameOnTop: multipleTeachersSelected
                              ? teacher.firstname
                              : false,
                          },
                        );
                        onMouseMoveElement = renderedNewSlot;
                        containerPerTeacher.appendChild(renderedNewSlot);
                      },
                    );
                  },
                );

                return containerPerTeacher;
              },
            );

            const specialContents = renderSpecialContents(
              opts.hours,
              minuteHeight,
              wdspecs.contents,
            );
            const weekdaycell = (
              <td style="position: relative">
                {specialContents}
                {renderedSlotsPerTeacher}
              </td>
            );
            return weekdaycell;
          })}
        </tr>
      </tbody>
    </table>
  );
}

function getLocalTimeFromMouseEventOnWeekdayCell(
  minuteHeight: number,
  startHour: number,
  weekdaycell: HTMLElement,
  ev: MouseEvent,
): joda.LocalTime {
  const minutesFromStart =
    (ev.pageY - weekdaycell.getBoundingClientRect().y) / minuteHeight;
  const time = joda.LocalTime.of(startHour).plusMinutes(minutesFromStart);
  return time;
}

function renderSlot(
  minuteHeight: number,
  startHour: number,
  sl: calendar_slot,
  opts: { addTeacherNameOnTop: false | string },
): HTMLElement {
  function calcTopStyle(time: joda.LocalTime) {
    const minutesFromStart = (time.toSecondOfDay() - startHour * 60 * 60) / 60;
    const top = Math.round(minutesFromStart) * minuteHeight;
    return `top: ${top}px;`;
  }

  function mkHeightStyle(duration: number) {
    return `height: ${duration * minuteHeight}px;`;
  }

  function renderTopTime(time: joda.LocalTime) {
    return <div className={planningstyle.top_time}>{time.format(HHmm)}</div>;
  }
  function renderBottomTime(time: joda.LocalTime, duration: number) {
    return (
      <div className={planningstyle.bottom_time}>
        {time.plusMinutes(duration).format(HHmm)}
      </div>
    );
  }

  function impl(time: joda.LocalTime, duration: number) {
    const st =
      `
    background: ${makeGradient(sl.colors)};
    color: ${Array.isArray(sl.colors) ? sl.colors[0].text : sl.colors.text};
    position: absolute;
    width: 100%;
    left: 0%;
  ` +
      mkHeightStyle(duration) +
      calcTopStyle(time);
    const st2 = `
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    text-align: center;
  `;
    const renderedSlot = (
      <a
        href={sl.onclick && "href" in sl.onclick ? sl.onclick.href : undefined}
        onclick={isFunction(sl.onclick) ? sl.onclick : undefined}
        className={
          style.a_around_button + planningstyle.slot + (sl.className || "")
        }
        style={st + (sl.onclick === null ? "pointer-events: none;" : "")}
      >
        <div style={st2}>
          {sl.contents}
          {renderTopTime(time)}
          {renderBottomTime(time, duration)}
          {isString(opts.addTeacherNameOnTop) ? (
            <div className={planningstyle.teacher_title}>
              {opts.addTeacherNameOnTop}
            </div>
          ) : (
            <span></span>
          )}
        </div>
      </a>
    );
    return renderedSlot;
  }

  if (isSource(sl.time)) {
    const timeS = sl.time;
    if (isSource(sl.duration)) {
      const durS = sl.duration;
      return <div>{dynVariadic([timeS, durS], ([t, d]) => impl(t, d))}</div>;
    } else {
      const dur = sl.duration;
      return <div>{dynVariadic([timeS], ([t]) => impl(t, dur))}</div>;
    }
  } else {
    const time = sl.time;
    if (isSource(sl.duration)) {
      return <div>{dynVariadic([sl.duration], ([d]) => impl(time, d))}</div>;
    } else {
      return impl(sl.time, sl.duration);
    }
  }
}

function renderSpecialContents(
  hours: [number, number],
  minuteHeight: number,
  contents: specialContents,
) {
  return contents === null ? (
    <span></span>
  ) : contents.tag === "holiday" ? (
    <div
      className={planningstyle.overlay}
      style="background-color: var(--grey-100);"
    >
      {contents.description}
    </div>
  ) : contents.tag === "past" ? (
    <div className={planningstyle.overlay}></div>
  ) : contents.tag === "today" ? (
    renderTimeline(hours, minuteHeight)
  ) : (
    checkAllCasesHandled(contents)
  );
}

function renderTimeline(
  hours: [number, number],
  minuteHeight: number,
): HTMLElement[] | HTMLElement {
  const nowS: Source<joda.LocalTime> = new Source(
    joda.LocalTime.now(joda.ZoneId.SYSTEM),
  );
  const int = window.setInterval(() => {
    nowS.set(joda.LocalTime.now(joda.ZoneId.SYSTEM));
  }, 30 * 1000);
  scheduleForCleanup(() => window.clearInterval(int));

  const maxHeight = (hours[1] - hours[0]) * 60 * minuteHeight;

  return dynStyle(
    nowS,
    (now) => {
      const dur = now.hour() * 60 + now.minute() - hours[0] * 60;
      if (dur <= 0) {
        return "display: none;";
      }
      const height = Math.min(dur * minuteHeight, maxHeight);
      return `height: ${height}px`;
    },
    <div className={planningstyle.overlay + planningstyle.timeline}></div>,
  );
}

function elementOrParentHasClass(el: HTMLElement, className: string): boolean {
  return (
    el.classList.contains(className.trim()) ||
    (!!el.parentElement && elementOrParentHasClass(el.parentElement, className))
  );
}

function isSource<T>(t: T | Source<T>): t is Source<T> {
  return isObject(t) && "observe" in t;
}
